/**
 * Productivity Integrations Settings Component
 * BYOP configuration for productivity tools: Asana, Google Drive, etc.
 */

import React, { useState, useEffect } from 'react';
import {
  Briefcase,
  FolderOpen,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  ExternalLink,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Trash2,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';

interface ProductivityIntegration {
  id: number;
  integration_type: string;
  integration_name: string | null;
  is_active: boolean;
  has_oauth_credentials: boolean;
  is_configured: boolean;
  configured_at: string | null;
  config: Record<string, any> | null;
}

interface ProductivityIntegrationsProps {
  organizationId: number;
  onSettingsChange?: () => void;
}

const INTEGRATION_INFO = {
  asana: {
    name: 'Asana',
    icon: '📋',
    description: 'Task and project management',
    setupUrl: 'https://app.asana.com/0/developer-console',
    setupInstructions: [
      'Go to Asana Developer Console',
      'Create a new OAuth app',
      'Set Redirect URI to your callback URL',
      'Copy Client ID and Client Secret',
    ],
  },
  google_drive: {
    name: 'Google Drive',
    icon: '📁',
    description: 'Document storage and sharing',
    setupUrl: 'https://console.cloud.google.com/apis/credentials',
    setupInstructions: [
      'Go to Google Cloud Console',
      'Create OAuth 2.0 credentials',
      'Enable Google Drive API',
      'Set Authorized redirect URIs',
      'Copy Client ID and Client Secret',
    ],
  },
  microsoft_onedrive: {
    name: 'OneDrive',
    icon: '☁️',
    description: 'Microsoft cloud storage',
    setupUrl: 'https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps',
    setupInstructions: [
      'Go to Azure Portal > App registrations',
      'Register a new application',
      'Add redirect URI',
      'Create a client secret',
      'Grant Files.ReadWrite permissions',
    ],
  },
  // Slack lives in the "Messaging (Slack/Teams)" tab now (full OAuth workspace
  // connect via WorkspaceMessagingIntegrations) — removed here to avoid two
  // conflicting Slack setup paths.
};

export function ProductivityIntegrations({
  organizationId,
  onSettingsChange,
}: ProductivityIntegrationsProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [integrations, setIntegrations] = useState<ProductivityIntegration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedIntegration, setExpandedIntegration] = useState<string | null>(null);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState<Record<string, {
    client_id: string;
    client_secret: string;
    redirect_uri: string;
  }>>({});

  useEffect(() => {
    fetchIntegrations();
  }, [organizationId]);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const response = await authFetch(getWeaveinApiUrl('/api/byop/productivity-integrations'));
      if (response.ok) {
        const data = await response.json();
        setIntegrations(data.integrations || []);
      }
    } catch (err) {
      console.error('Error fetching integrations:', err);
      setError('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCredentials = async (integrationType: string) => {
    const data = formData[integrationType];
    if (!data) return;

    try {
      setSaving(true);

      const redirectUri = data.redirect_uri ||
        `${getWeaveinApiUrl('/api')}/oauth/${integrationType}/callback`;

      const response = await authFetch(getWeaveinApiUrl('/api/byop/productivity-integrations'), {
        method: 'POST',
        jsonBody: {
            integration_type: integrationType,
            oauth_client_id: data.client_id,
            oauth_client_secret: data.client_secret,
            oauth_redirect_uri: redirectUri,
            is_active: true,
          }
      });

      if (response.ok) {
        fetchIntegrations();
        onSettingsChange?.();
        setExpandedIntegration(null);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save credentials');
      }
    } catch (err) {
      console.error('Error saving credentials:', err);
      setError('Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveCredentials = async (integrationType: string) => {
    if (!confirm('Are you sure you want to remove these credentials?')) return;

    try {
      setSaving(true);
      const response = await authFetch(getWeaveinApiUrl(`/api/byop/productivity-integrations/${integrationType}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        fetchIntegrations();
        onSettingsChange?.();
      }
    } catch (err) {
      console.error('Error removing credentials:', err);
    } finally {
      setSaving(false);
    }
  };

  const getIntegrationStatus = (type: string) => {
    return integrations.find(i => i.integration_type === type);
  };

  const renderIntegrationCard = (type: string) => {
    const info = INTEGRATION_INFO[type as keyof typeof INTEGRATION_INFO];
    const status = getIntegrationStatus(type);
    const isExpanded = expandedIntegration === type;
    const form = formData[type] || { client_id: '', client_secret: '', redirect_uri: '' };

    return (
      <div
        key={type}
        className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
          onClick={() => setExpandedIntegration(isExpanded ? null : type)}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">{info.icon}</span>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white">{info.name}</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">{info.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {status?.has_oauth_credentials ? (
              <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                <CheckCircle className="h-4 w-4" />
                Configured
              </span>
            ) : (
              <span className="flex items-center gap-1 text-sm text-gray-400">
                <AlertCircle className="h-4 w-4" />
                Not configured
              </span>
            )}
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/50">
            {/* Setup Instructions */}
            <div className="mb-4">
              <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                Setup Instructions
              </h5>
              <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-1 list-decimal list-inside">
                {info.setupInstructions.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
              <a
                href={info.setupUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline mt-2"
              >
                Open {info.name} Developer Console
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>

            {/* Credentials Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Client ID
                </label>
                <input
                  type="text"
                  value={form.client_id}
                  onChange={(e) => setFormData({
                    ...formData,
                    [type]: { ...form, client_id: e.target.value }
                  })}
                  placeholder="Enter OAuth Client ID"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Client Secret
                </label>
                <div className="relative">
                  <input
                    type={showSecrets[type] ? 'text' : 'password'}
                    value={form.client_secret}
                    onChange={(e) => setFormData({
                      ...formData,
                      [type]: { ...form, client_secret: e.target.value }
                    })}
                    placeholder="Enter OAuth Client Secret"
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecrets({ ...showSecrets, [type]: !showSecrets[type] })}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showSecrets[type] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Redirect URI (auto-generated)
                </label>
                <input
                  type="text"
                  value={form.redirect_uri || `${getWeaveinApiUrl('/api')}/oauth/${type}/callback`}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                  readOnly
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add this URL to your OAuth app's redirect URIs
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                <div>
                  {status?.has_oauth_credentials && (
                    <button
                      onClick={() => handleRemoveCredentials(type)}
                      disabled={saving}
                      className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove Credentials
                    </button>
                  )}
                </div>
                <button
                  onClick={() => handleSaveCredentials(type)}
                  disabled={saving || !form.client_id || !form.client_secret}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving && <RefreshCw className="h-4 w-4 animate-spin" />}
                  Save Credentials
                </button>
              </div>
            </div>

            {/* Status Info */}
            {status?.configured_at && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Last configured: {fmt.dateTime(status.configured_at)}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-purple-600" />
          Productivity Integrations
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Connect your organization's productivity tools for task management, file storage, and team collaboration.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Integration Cards */}
      <div className="space-y-4">
        {Object.keys(INTEGRATION_INFO).map(renderIntegrationCard)}
      </div>

      {/* Help Text */}
      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
        <h4 className="text-sm font-medium text-purple-900 dark:text-purple-100 mb-1">
          Integration Benefits
        </h4>
        <ul className="text-sm text-purple-700 dark:text-purple-300 space-y-1">
          <li>• <strong>Asana:</strong> Sync tasks and track member case management progress</li>
          <li>• <strong>Google Drive:</strong> Store and share member documents securely</li>
          <li>• <strong>OneDrive:</strong> Microsoft 365 document integration</li>
        </ul>
      </div>
    </div>
  );
}

export default ProductivityIntegrations;
